import { useCallback, useState } from 'react';
import { useRxCollection, useRxData } from '@/providers/rxdb/rx-hooks';
import type { MangoQuery, RxCollection, RxDocument } from 'rxdb';

import { getAuthUser } from '@flintmint/web-shared/hooks/use-auth-user';

import { duplicateNameRegexSource, getCurrDatetime, getDuplicateNameBase, nextDuplicateName } from '@flintmint/core-shared/utils';
import { buildArchivePatch, isArchivePartitioned, notArchivedSelector, type RxJSON } from '@flintmint/core-shared/rxdb';
import { captureDocumentLifecycleEvent } from './document-lifecycle-analytics';

export type MutationOptions = {
  id?: string; // forces id instead of generating a new one
  createdAt?: string; // forces createdAt instead of generating a new one
  ifUnmodifiedSince?: string; // optimistic-concurrency guard: patch only if the doc's updatedAt still matches
  onSuccess?: (data?: RxDocument<any>) => void;
  onError?: (error: any) => void;
};

export class StaleDocumentWriteError extends Error {
  constructor(public readonly documentId: string) {
    super(`Document ${documentId} was modified since the write was prepared`);
    this.name = 'StaleDocumentWriteError';
  }
}

export const useRxDocument = <T>(collectionName: string, id: string | undefined, isEnabled = true) => {
  const queryConstructor = useCallback((collection: any) => collection.findOne(id), [id]);
  const { result, isFetching } = useRxData<T>(collectionName, queryConstructor);

  if (!id || !isEnabled) {
    return { data: undefined, isLoading: false };
  }

  const data = result[0] as RxDocument<T>;
  // @ts-ignore check to see if collection.findOne returned a correct result, sometimes it returns wrong result if the passed id doesn't exist
  return { data: data?.id === id ? data : undefined, isLoading: isFetching };
};

export const useRxDocumentLazy = <T extends { userId: string; id: string }>(collectionName: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const collection = useRxCollection<T>(collectionName);

  const query = useCallback(
    async (id: string): Promise<RxDocument<T> | null> => {
      if (!collection) return null;

      try {
        setIsLoading(true);
        return await findDocById(collection, id);
      } finally {
        setIsLoading(false);
      }
    },
    [collection]
  );

  return { query, isLoading: isLoading || !collection };
};

const findDocById = async <T extends { id: string }>(collection: RxCollection<T>, id: string): Promise<RxDocument<T> | null> => {
  const doc = await collection.findOne(id).exec();
  // check to see if collection.findOne returned a correct result, sometimes it returns wrong result if the passed id doesn't exist
  return doc?.id === id ? doc : null;
};

const useRxDocumentsBase = <T extends { userId: string }>(collectionName: string, query?: MangoQuery<T>) => {
  const collection = useRxCollection<T>(collectionName);
  const { result, isFetching } = useRxData<T>(collectionName, (collection) => (query ? collection.find(query) : undefined));
  return { data: result as RxDocument<T>[], isLoading: isFetching || !collection };
};

export interface RxQueryParams<T> {
  selector?: MangoQuery<T>['selector'];
  ids?: string[];
  tagIds?: string[];
  availabilityScopes?: string[];
  sort?: MangoQuery<T>['sort'];
  limit?: number;
}

interface UseRxDocumentsOptions {
  enabled?: boolean;
}

export const useRxDocuments = <T extends { userId: string; id: string }>(
  collectionName: string,
  queryParams: RxQueryParams<T> = {},
  options: UseRxDocumentsOptions = {}
) => {
  const { enabled = true } = options;
  const query = enabled ? buildMangoQuery<T>(queryParams) : undefined;
  return useRxDocumentsBase<T>(collectionName, query);
};

export const useRxDocumentsLazy = <T extends { userId: string; id: string }>(collectionName: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const collection = useRxCollection<T>(collectionName);

  const query = useCallback(
    async (queryParams: RxQueryParams<T> = {}): Promise<RxDocument<T>[]> => {
      if (!collection) return [];

      try {
        setIsLoading(true);
        const query = buildMangoQuery<T>(queryParams);
        const documents = await collection.find(query).exec();
        return documents;
      } finally {
        setIsLoading(false);
      }
    },
    [collection]
  );

  return { query, isLoading: isLoading || !collection };
};

const buildMangoQuery = <T>({ selector, ids, tagIds, availabilityScopes, sort, limit }: RxQueryParams<T> = {}): MangoQuery<T> => {
  const selectors = [
    ...(selector ? [selector] : []),
    ...(ids ? [{ id: { $in: ids } }] : []),
    ...(tagIds?.length ? [{ tagIds: { $in: tagIds } }] : []),
    ...(availabilityScopes?.length
      ? [{ $or: [{ availabilityScope: { $in: availabilityScopes } }, { availabilityScope: { $exists: false } }] }]
      : []),
    notArchivedSelector,
  ];

  return {
    ...(selectors.length === 1 && { selector: selectors[0] }),
    ...(selectors.length > 1 && { selector: { $and: selectors } }),
    ...(sort && { sort }),
    ...(limit && { limit }),
  } as MangoQuery<T>;
};

export type CreateMutation<T> = (payload: Partial<T>, mutationOptions?: MutationOptions) => Promise<any>;

export const useRxCreateMutation = <T>(collectionName: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const collection = useRxCollection(collectionName);
  const { id: userId } = getAuthUser();

  const mutate = useCallback(
    async (payload: Partial<T>, mutationOptions: MutationOptions = {}): Promise<RxDocument<T>> => {
      try {
        setIsLoading(true);
        const now = getCurrDatetime();
        // Collections partitioned by the archived flag need it present from creation so the
        // doc is visible to the active replication partition's pull on other devices.
        const archiveDefaults = isArchivePartitioned(collection!) ? buildArchivePatch(false) : {};

        const createdRxDoc = await collection!.upsert({
          ...archiveDefaults,
          ...payload,
          id: mutationOptions.id || crypto.randomUUID(),
          userId,
          updatedAt: now,
          createdAt: mutationOptions.createdAt || now,
        });

        captureDocumentLifecycleEvent('document_created', collectionName, [createdRxDoc]);
        mutationOptions.onSuccess?.(createdRxDoc);
        return createdRxDoc as RxDocument<T>;
      } catch (e) {
        console.error(e);
        mutationOptions.onError?.(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [collection, collectionName, userId]
  );

  return { mutate, isLoading };
};

export const useRxPatchMutation = <T>() => {
  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback<PatchMutation<T>>(
    async (rxDoc: RxDocument<T>, payload: Partial<T>, mutationOptions: MutationOptions = {}): Promise<RxDocument<T>> => {
      try {
        setIsLoading(true);
        const sanitizedPayload = replaceUndefinedWithNull<T>(payload);
        const updateData = {
          ...sanitizedPayload,
          ...(shouldModifyUpdatedAt(sanitizedPayload) && { updatedAt: getCurrDatetime() }),
        };

        const updatedRxDoc = await patchWithFreshnessGuard(rxDoc, updateData, mutationOptions.ifUnmodifiedSince);
        mutationOptions.onSuccess?.(updatedRxDoc);
        return updatedRxDoc;
      } catch (e) {
        if (e instanceof StaleDocumentWriteError) {
          throw e;
        }
        console.error(e);
        mutationOptions.onError?.(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return { mutate, isLoading };
};

const patchWithFreshnessGuard = <T>(
  rxDoc: RxDocument<T>,
  updateData: Partial<T>,
  ifUnmodifiedSince?: string
): Promise<RxDocument<T>> => {
  if (ifUnmodifiedSince === undefined) {
    return rxDoc.incrementalPatch(updateData);
  }

  return rxDoc.incrementalModify((docData) => {
    const { id, updatedAt } = docData as unknown as { id: string; updatedAt?: string };
    if (updatedAt !== ifUnmodifiedSince) {
      throw new StaleDocumentWriteError(id);
    }
    return { ...docData, ...updateData };
  });
};

export const useRxDeleteById = <T>(collectionName: string) => {
  const { mutate, isLoading } = useRXDeleteByIds<T>(collectionName);

  const mutateSingle = useCallback(
    (id: string, mutationOptions: MutationOptions = {}) => mutate([id], mutationOptions),
    [mutate]
  );

  return { mutate: mutateSingle, isLoading };
};

export const useRXDeleteByIds = <T>(collectionName: string) => {
  const collection = useRxCollection<T>(collectionName);
  const [isLoading, setIsLoading] = useState(false);

  const mutate = useCallback(
    async (ids: string[], mutationOptions: MutationOptions = {}) => {
      if (!ids.length) {
        return;
      }
      try {
        setIsLoading(true);

        const docsMap = await collection!.findByIds(ids).exec();
        const docs = Array.from(docsMap.values());
        await Promise.all(docs.map((doc) => doc.incrementalRemove()));

        captureDocumentLifecycleEvent('document_deleted', collectionName, docs);
        mutationOptions.onSuccess?.();
      } catch (e) {
        console.error(e);
        mutationOptions.onError?.(e);
        throw e;
      } finally {
        setIsLoading(false);
      }
    },
    [collection, collectionName]
  );

  return { mutate, isLoading };
};

export type PatchMutation<T> = (
  rxDoc: RxDocument<T>,
  payload: Partial<T>,
  mutationOptions?: MutationOptions
) => Promise<RxDocument<T>>;

// deprecated
export const useRxUpdateMutationById = <T>(collectionName: string) => {
  const collection = useRxCollection<T>(collectionName);
  const { mutate: patchMutation } = useRxPatchMutation<T>();
  const [isLoading, setIsLoading] = useState(false);

  const findAndPatch = useCallback(
    async (id: string, payload: Partial<T>, mutationOptions: MutationOptions = {}) => {
      try {
        setIsLoading(true);

        const document = await collection!.findOne(id).exec();
        if (!document) {
          throw new Error(`Document with ID ${id} not found`);
        }

        return await patchMutation(document, payload, mutationOptions);
      } finally {
        setIsLoading(false);
      }
    },
    [collection, patchMutation]
  );

  return { mutate: findAndPatch, isLoading };
};

const shouldModifyUpdatedAt = (payload: Partial<RxJSON>) => {
  return Object.keys(payload).some((key) => !['aliases', 'tagIds', 'archived', 'archivedAt'].includes(key));
};

export const useDuplicateMutation = <T extends { name?: string; variant?: string }>(
  collectionName: string,
  overrides?: Partial<T>
) => {
  const collection = useRxCollection<T>(collectionName);

  const { mutate: createDocument, isLoading: isCreatingDocument } = useRxCreateMutation<T>(collectionName);

  const [isLoading, setIsLoading] = useState(false);

  const duplicateDocument = useCallback(
    async (id: string, mutationOptions: MutationOptions = {}) => {
      setIsLoading(true);

      try {
        const originalDocument = await collection!.findOne(id).exec();
        if (!originalDocument) {
          throw new Error(`${collectionName.slice(0, -1)} not found`);
        }

        const document = originalDocument.toMutableJSON();
        const name =
          document.name && !overrides?.name
            ? await nextSiblingDuplicateName(collection!, document.name, document.variant)
            : undefined;

        const newDocument = {
          ...document,
          ...(name && { name }),
          ...overrides,
        };

        await createDocument(newDocument, mutationOptions);
      } finally {
        setIsLoading(false);
      }
    },
    [collection, collectionName, createDocument, overrides]
  );

  return {
    mutate: duplicateDocument,
    isLoading: isCreatingDocument || isLoading,
  };
};

// Numbering pool: live (non-archived) docs of the same collection and, where the schema has
// one, the same variant (notes vs templates), whose name is a numbered duplicate of the
// source's base name. RxDB queries skip deleted docs on their own. `name` is unindexed, so
// this scans the live docs; acceptable for a one-off user action.
const nextSiblingDuplicateName = async (collection: RxCollection, name: string, variant?: string): Promise<string> => {
  const base = getDuplicateNameBase(name);

  const siblings = await collection
    .find({
      selector: {
        ...notArchivedSelector,
        ...(variant && { variant }),
        name: { $regex: duplicateNameRegexSource(base) },
      },
    })
    .exec();

  return nextDuplicateName(
    base,
    siblings.map((sibling) => sibling.name)
  );
};

type WriterMutation<T> = {
  mutate: (payload: Partial<T>, mutationOptions?: MutationOptions) => Promise<RxDocument<T>>;
};

type UseRxWriterParams<T> = {
  collectionName: string;
  create: WriterMutation<T>;
  patch: { mutate: PatchMutation<T> };
  // Set when the target is a not-yet-saved draft (e.g. a draft route, or the cortex origin note's
  // `isDraft`); without it a missing target throws.
  createIfMissing?: boolean;
};

// Draft target resolutions ("stored doc, else create") in flight per `${collectionName}:${id}`,
// shared by every writer instance. Values are `DraftTarget<T>` for the collection in the key.
const draftTargets = new Map<string, Promise<unknown>>();

type DraftTarget<T> = { doc: RxDocument<T>; created: boolean };

// Writes target `id`: patches the rendered doc if it is that id, else the stored doc with that id. Without an id it creates.
export const useRxWriter = <T extends { id: string }>(
  rxDoc: RxDocument<T> | undefined,
  id: string | undefined,
  { collectionName, create, patch, createIfMissing = false }: UseRxWriterParams<T>
) => {
  const { mutate: createMutation } = create;
  const { mutate: patchMutation } = patch;
  const collection = useRxCollection<T>(collectionName);

  const mutate = useCallback(
    async (payload: Partial<T>, mutationOptions?: MutationOptions): Promise<RxDocument<T>> => {
      const renderedTarget = rxDoc && (!id || rxDoc.id === id) ? rxDoc : undefined;
      if (renderedTarget) {
        return patchMutation(renderedTarget, payload, mutationOptions);
      }
      if (!id) {
        return createMutation(payload, mutationOptions);
      }

      // A not-ready collection cannot tell a missing target from an unloaded one, so never create on it.
      if (!collection) {
        throw new Error(`${collectionName} collection not ready`);
      }

      if (!createIfMissing) {
        const storedDoc = await findDocById(collection, id);
        if (!storedDoc) {
          throw new Error(`${collectionName} document ${id} not found`);
        }
        return patchMutation(storedDoc, payload, mutationOptions);
      }

      // Several writers can hit the same draft id before it is stored; a second create would reset
      // the first's fields to create defaults. The first reserves the id before its lookup, so a
      // later writer never runs its own lookup-then-create and patches the resolved target instead.
      const targetKey = `${collectionName}:${id}`;
      const reservedTarget = draftTargets.get(targetKey);
      if (reservedTarget) {
        // The key carries the collection name, so the reservation resolves to a doc of this T.
        const { doc } = await (reservedTarget as Promise<DraftTarget<T>>);
        return patchMutation(doc, payload, mutationOptions);
      }

      const target = resolveDraftTarget<T>(collection, id, payload, mutationOptions, createMutation);
      draftTargets.set(targetKey, target);
      try {
        const { doc, created } = await target;
        return created ? doc : await patchMutation(doc, payload, mutationOptions);
      } finally {
        if (draftTargets.get(targetKey) === target) {
          draftTargets.delete(targetKey);
        }
      }
    },
    [rxDoc, id, collection, collectionName, createIfMissing, createMutation, patchMutation]
  );

  return { mutate };
};

const resolveDraftTarget = async <T extends { id: string }>(
  collection: RxCollection<T>,
  id: string,
  payload: Partial<T>,
  mutationOptions: MutationOptions | undefined,
  createMutation: WriterMutation<T>['mutate']
): Promise<DraftTarget<T>> => {
  const storedDoc = await findDocById(collection, id);
  if (storedDoc) {
    return { doc: storedDoc, created: false };
  }
  return { doc: await createMutation(payload, { ...mutationOptions, id }), created: true };
};

const replaceUndefinedWithNull = <T>(payload: Partial<T>) => {
  // Firebase can't handle undefined values so they need to be replaced with null
  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [key, value === undefined ? null : value])
  ) as Partial<T>;
};
